/* eslint-disable max-lines-per-function */
import { compact, isEmpty, noop } from 'lodash';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import {
   ReceptionServiceWithPractitionersSimple,
   ReceptionServiceWithPractitionersSimpleQuery,
} from '@dreryk/egabinet-private-medical-service-frontend-api';

import { useLazyLoadedDataConfigurationByCqrsQuery } from '@dreryk/drerykweb-main-frontend';
import { Ordering } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   EmptyStatePlaceholder,
   HorizontalAlign,
   JustifyContent,
   KeyExtractorRendererKeyExtractor,
   Link,
   RowDetailsExpander,
   RowDetailsRenderer,
   Table,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';
import { TextPattern } from '@dreryk/watermelon';

import { receptionServiceTranslationKeys } from '../../translations';
import { ReceptionServiceCreateModalOpener } from '../create/ReceptionServiceCreateModalOpener';

import { ReceptionServiceDurationCell } from './cells/ReceptionServiceDurationCell';
import { ReceptionServiceNameCell } from './cells/ReceptionServiceNameCell';
import { ReceptionServiceOptionsCell } from './cells/ReceptionServiceOptionsCell';
import { ReceptionServicePriceCell } from './cells/ReceptionServicePriceCell';
import { PractitionerRowDetails } from './rowDetails/PractitionerRowDetails';

const StyledEmptyStatePlaceholder = styled(EmptyStatePlaceholder)`
   width: 537px;
`;

StyledEmptyStatePlaceholder.displayName = 'StyledEmptyStatePlaceholder';

type ReceptionServiceTablePlaceholderProps = {
   readonly noSearchResults: boolean;
};

const ReceptionServiceTablePlaceholder = (props: ReceptionServiceTablePlaceholderProps): JSX.Element => {
   if (props.noSearchResults) {
      return (
         <StyledEmptyStatePlaceholder
            testId={'noSearchResults'}
            heading={receptionServiceTranslationKeys.table.placeholder.noSearchResults.heading}
            fullLength
         />
      );
   }

   return (
      <StyledEmptyStatePlaceholder
         testId={'noReceptionService'}
         heading={receptionServiceTranslationKeys.table.placeholder.noService.heading}
         subheading={receptionServiceTranslationKeys.table.placeholder.noService.subheading}
         fullLength
      >
         <ReceptionServiceCreateModalOpener>
            {({ openModal }) => (
               <Link
                  testId={'receptionServiceCreate'}
                  label={receptionServiceTranslationKeys.table.placeholder.noService.link}
                  onClick={openModal}
               />
            )}
         </ReceptionServiceCreateModalOpener>
      </StyledEmptyStatePlaceholder>
   );
};

const DEFAULT_ORDERINGS: Ordering<ReceptionServiceWithPractitionersSimple>[] = [
   {
      descending: false,
      property: 'name',
   },
];

type ReceptionServiceTableProps = {
   readonly searchText: string | null;
   readonly hasMorePractitioners: boolean;
};

export const ReceptionServiceTable = (props: ReceptionServiceTableProps): JSX.Element => {
   const dataConfiguration = useLazyLoadedDataConfigurationByCqrsQuery<ReceptionServiceWithPractitionersSimple>(() => {
      const query = new ReceptionServiceWithPractitionersSimpleQuery();
      if (props.searchText) {
         query.name = TextPattern.anywhereIgnoreCase(props.searchText);
      }
      return query;
   }, DEFAULT_ORDERINGS);

   const columns = useMemo((): TableColumnConfiguration<ReceptionServiceWithPractitionersSimple>[] => {
      const columnsWidth = createDynamicColumnsWidth({
         name: '100%',
         price: '190px',
         practitioners: props.hasMorePractitioners ? '90px' : '0px',
         options: '64px',
         expander: props.hasMorePractitioners ? '50px' : '0px',
         estimatedDuration: '130px',
      });

      return compact([
         {
            name: 'name',
            label: receptionServiceTranslationKeys.table.headers.name,
            width: columnsWidth.name,
            accessor: noop,

            renderer: ({ bean }: ValueRendererProps<ReceptionServiceWithPractitionersSimple>): JSX.Element => (
               <ReceptionServiceNameCell name={bean.name} color={bean.color} />
            ),
         },
         {
            name: 'price',
            label: receptionServiceTranslationKeys.table.headers.price,
            width: columnsWidth.price,
            textAlign: HorizontalAlign.RIGHT,
            justifyContent: JustifyContent.END,
            accessor: noop,

            renderer: ({ bean }: ValueRendererProps<ReceptionServiceWithPractitionersSimple>): JSX.Element => (
               <ReceptionServicePriceCell
                  defaultMinPrice={bean.minPrice}
                  defaultMaxPrice={bean.maxPrice}
                  practitioners={bean.practitioners}
               />
            ),
            sortingDisabled: true,
         },
         {
            name: 'estimatedDuration',
            label: receptionServiceTranslationKeys.table.headers.estimatedDuration,
            width: columnsWidth.estimatedDuration,
            textAlign: HorizontalAlign.RIGHT,
            justifyContent: JustifyContent.END,
            accessor: noop,

            renderer: ({ bean }: ValueRendererProps<ReceptionServiceWithPractitionersSimple>): JSX.Element => (
               <ReceptionServiceDurationCell
                  defaultEstimatedDuration={bean.estimatedDuration}
                  practitioners={bean.practitioners}
               />
            ),
            sortingDisabled: true,
         },
         props.hasMorePractitioners && {
            name: 'practitioners',
            label: receptionServiceTranslationKeys.table.headers.practitioners,
            width: columnsWidth.practitioners,
            textAlign: HorizontalAlign.RIGHT,
            justifyContent: JustifyContent.END,
            accessor: (bean) => bean.practitioners.length,
            sortingDisabled: true,
         },
         {
            name: 'options',
            label: '',
            width: columnsWidth.options,
            accessor: noop,

            renderer: ({ bean }: ValueRendererProps<ReceptionServiceWithPractitionersSimple>): JSX.Element => (
               <ReceptionServiceOptionsCell
                  id={bean.id}
                  name={bean.name}
                  hasMorePractitioners={bean.practitioners.length > 1}
               />
            ),
            sortingDisabled: true,
         },
         props.hasMorePractitioners && {
            name: 'expander',
            label: '',
            width: columnsWidth.expander,
            accessor: noop,
            textAlign: HorizontalAlign.CENTER,
            sortingDisabled: true,

            renderer: (renderProps: ValueRendererProps<ReceptionServiceWithPractitionersSimple>): JSX.Element => {
               return (
                  <RowDetailsExpander
                     {...renderProps}
                     testId={'expander'}
                     disabled={isEmpty(renderProps.bean.practitioners)}
                  />
               );
            },
         },
      ]);
   }, [props.hasMorePractitioners]);

   const rowDetailsRenderer = useCallback<RowDetailsRenderer<ReceptionServiceWithPractitionersSimple>>(({ bean }) => {
      return bean.practitioners.map(
         ({ practitioner, minPrice, maxPrice, id: practitionerReceptionServiceId, estimatedDuration }) => (
            <PractitionerRowDetails
               key={practitioner.id}
               practitionerReceptionServiceId={practitionerReceptionServiceId}
               practitionerId={practitioner.id}
               practitionerName={`${practitioner.firstName.asString()} ${practitioner.lastName.asString()}`}
               practitionerSpecialtyNames={practitioner.specialtyNames}
               minPrice={minPrice ?? bean.minPrice}
               maxPrice={maxPrice ?? bean.maxPrice}
               receptionServiceId={bean.id}
               receptionServiceName={bean.name}
               hasMorePractitioners={bean.practitioners.length > 1}
               estimatedDuration={estimatedDuration ?? bean.estimatedDuration}
            />
         )
      );
   }, []);

   const idKeyExtractor: KeyExtractorRendererKeyExtractor<ReceptionServiceWithPractitionersSimple> = (item) =>
      item.id.asNumber();

   return (
      <Table
         testId={'receptionService'}
         keyExtractor={idKeyExtractor}
         dataConfiguration={dataConfiguration}
         columns={columns}
         placeholder={<ReceptionServiceTablePlaceholder noSearchResults={Boolean(props.searchText)} />}
         rowDetailsRenderer={rowDetailsRenderer}
         pageSize={1000}
      />
   );
};
